import {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react'
import _ from 'lodash'
// Components
import InputCheckbox from '../forms/input-checkbox'
import InputImageupload from '../forms/input-imageupload'
import InputSelect from '../forms/input-select'
import InputTextarea from '../forms/input-textarea'
import InputTextfield from '../forms/input-textfield'
import ClientText from '../client-text'
// Types
import type {AdditionalFormField, StripePayload, UploadcareFile} from '../../types/submission-wizard'
// Helpers
import {replaceObjectInArrayById} from '../../utils/array-helpers'
// Styles
import styles from '../submission-wizard/add-metadata-step.module.scss'
import wizardStyles from '../submission-wizard/submission-wizard.module.scss'
import formStyles from '../forms/forms.module.scss'

interface AddMetadataStepProps {
  stripePayload: StripePayload
  submissionNameFieldLabel: string
  questionDescription: string | null
  sendStripePayload: (payload: StripePayload) => void
  uploadcareFiles: UploadcareFile[]
}

const AddMetadataStep = ({
  stripePayload,
  submissionNameFieldLabel,
  questionDescription,
  sendStripePayload,
  uploadcareFiles,
}: AddMetadataStepProps) => {
  const stepRef = useRef<HTMLDivElement>(null)

  const [requiresScrolling, setRequiresScrolling] = useState(false)

  const [formFields, setFormFields] = useState<AdditionalFormField[]>(() => stripePayload.additionalFormFields)
  const [submissionName, setSubmissionName] = useState<string>(() => stripePayload.submissionName)

  type FormInputEvent = ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | { target: { value: string } }

  const handleFieldChange = (syntheticEvent: FormInputEvent | string, fieldId: string) => {
    let newStripePayload = _.cloneDeep(stripePayload)
    let newFieldValue: string | boolean = typeof syntheticEvent === 'string' ? syntheticEvent : syntheticEvent.target?.value || ''
    if (fieldId === 'submissionName') {
      setSubmissionName(newFieldValue as string)
      newStripePayload['submissionName'] = newFieldValue as string
    } else {
      const formFields = _.clone(newStripePayload.additionalFormFields)
      const foundField = _.find(formFields, field => field.id === fieldId)
      if (!foundField) return
      if (foundField.type === 'checkbox' && typeof syntheticEvent !== 'string') {
        newFieldValue = (syntheticEvent.target as HTMLInputElement).checked
      }
      if (foundField.type === 'imageupload') {
        newFieldValue = syntheticEvent as string // syntheticEvent is the url returned from component
      }
      // if (foundField.type === 'selectdropdown') {
      // TODO: Since the rank of a selectdropdown may change after someone has submitted it
      //       How should we address the value of what the submitter selects? If it's rank, it'll muddy the data
      //       If it's name, that may change too. Ideally, we would have a unique id
      // }
      const newAdditionalFieldValue: AdditionalFormField = {
        ...foundField,
        value: newFieldValue,
      }
      let newAdditionalFieldValues = replaceObjectInArrayById(formFields, fieldId, newAdditionalFieldValue)
      setFormFields(newAdditionalFieldValues as AdditionalFormField[])

      newStripePayload['additionalFormFields'] = newAdditionalFieldValues as AdditionalFormField[]
    }

    sendStripePayload(newStripePayload)
  }

  const handleWindowResize = useCallback(() => {
    if (!stepRef?.current) return
    const rect = stepRef.current.getBoundingClientRect()
    setRequiresScrolling(rect?.height + 118 > window.innerHeight)
  }, [])

  useEffect(() => {
    // Initialize once on mount
    handleWindowResize()
    // Attach and clean up the window resize listener
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [handleWindowResize])

  const handleElementResize = useCallback((entry: ResizeObserverEntry) => {
    const {height} = entry.contentRect
    const containerHeight = stepRef.current?.getBoundingClientRect()?.height || 0
    const offset = containerHeight - height + 118
    setRequiresScrolling(height + offset > window.innerHeight)
  }, [])

  useEffect(() => {
    const node = stepRef.current
    if (!node || typeof ResizeObserver === 'undefined') return

    const timer = setTimeout(() => {
      handleWindowResize()
    }, 100)

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        handleElementResize(entry)
      }
    })
    resizeObserver.observe(node)

    return () => {
      clearTimeout(timer)
      resizeObserver.disconnect()
    }
  }, [handleElementResize, handleWindowResize])

  return (
    <div
      className={`
        ${wizardStyles['__step-container']}
        ${styles['__container']}
        ${requiresScrolling ? styles['--requires-scrolling'] : ''}
      `}
    >
      <div
        ref={stepRef}
        className={`
          ${styles['add-metadata-step']}
          ${wizardStyles['__submission-wizard-step']}
        `}
      >
        <div className={wizardStyles['__step-header']}>
          <img
            src={_.first(uploadcareFiles)?.cdnUrl || 'https://ucarecdn.com/47a41f84-b865-4aeb-a53a-3b20cb168d46/-/preview/50x50/'}
            alt={stripePayload?.submissionName || 'Unnamed entry'}
            className={wizardStyles['__entry-thumbnail']}
          />
          <div className={wizardStyles['__entry-details']}>
            <h3
              className={`
                ${wizardStyles['__entry-name']}
                ${!stripePayload?.submissionName ? wizardStyles['--is-unnamed'] : ''}
              `}
            >
              {stripePayload?.submissionName || 'Unnamed entry'}
            </h3>
            <h3 className={wizardStyles['__entry-length']}>
              {`${uploadcareFiles.length || '0'} photo${uploadcareFiles.length > 1 || !uploadcareFiles.length ? 's' : ''}`}
            </h3>
          </div>
        </div>
        <form className={formStyles['__form']}>
          <fieldset className={formStyles['__fieldset']}>
            {/* Permanent 'name' field */}
            <InputTextfield
              id='required-name'
              label={`${submissionNameFieldLabel || 'Name'} (required)`}
              placeholder={submissionNameFieldLabel || 'Name'}
              initialValue={submissionName}
              errorMessage={null}
              onChange={(syntheticEvent: ChangeEvent<HTMLInputElement> | { target: { value: string } }) => handleFieldChange(syntheticEvent, 'submissionName')}
            />
            {formFields?.length > 0 && _.map(formFields, formField => {
              switch (formField.type) {
              case 'textfield':
                return (
                  <InputTextfield
                    id={formField.id}
                    label={`${formField.name}${formField.required ? ' (required)' : ''}`}
                    instructions={formField.field_instructions}
                    placeholder={formField.placeholder_text}
                    characterLimit={formField.max_character_limit}
                    initialValue={(formField.value as string) || ''}
                    errorMessage={null}
                    onChange={(syntheticEvent: ChangeEvent<HTMLInputElement> | { target: { value: string } }) =>
                      handleFieldChange(syntheticEvent, formField.id)}
                  />
                )
              case 'textarea':
                return (
                  <InputTextarea
                    id={formField.id}
                    label={`${formField.name}${formField.required ? ' (required)' : ''}`}
                    instructions={formField.field_instructions}
                    placeholder={formField.placeholder_text}
                    characterLimit={formField.max_character_limit}
                    initialValue={(formField.value as string) || ''}
                    errorMessage={null}
                    onChange={(syntheticEvent: ChangeEvent<HTMLTextAreaElement> | { target: { value: string } }) =>
                      handleFieldChange(syntheticEvent, formField.id)}
                  />
                )
              case 'imageupload':
                return <InputImageupload
                  id={formField.id}
                  label={formField.name}
                  instructions={formField.field_instructions}
                  errorMessage={null}
                  isRequired={formField.required}
                  onChange={(syntheticEvent: string) => handleFieldChange(syntheticEvent, formField.id)}
                />
              case 'checkbox':
                return (
                  <InputCheckbox
                    id={formField.id}
                    label={`${formField.name}${formField.required ? ' (required)' : ''}`}
                    instructions={formField.field_instructions}
                    initialValue={formField.checkbox_mark_by_default}
                    onChange={(syntheticEvent: ChangeEvent<HTMLInputElement>) =>
                      handleFieldChange(syntheticEvent, formField.id)}
                  />
                )
              case 'selectdropdown':
                return (
                  <InputSelect
                    id={formField.id}
                    label={`${formField.name}${formField.required ? ' (required)' : ''}`}
                    instructions={formField.field_instructions}
                    options={_.map(formField.drop_down_choices, option => {
                      return {
                        key: `${formField.id}-${option.rank}`,
                        value: String(option.rank),
                        label: option.name,
                      }
                    })}
                    errorMessage={null}
                    onChange={(syntheticEvent: ChangeEvent<HTMLSelectElement>) =>
                      handleFieldChange(syntheticEvent, formField.id)}
                  />
                )
              default:
                break
              }
            })}
          </fieldset>
          {questionDescription && <ClientText clientText={questionDescription} />}
        </form>
      </div>
    </div>
  )
}

export default AddMetadataStep
